import styled, { css } from 'styled-components/macro'
import {
  BaseColor,
  borderRadius,
  elevation,
  linkReset,
  LineHeight,
  rem,
  spacing,
  textEllipsis,
  themeColor,
  transitions,
  Typography,
} from '@unbounded/unbounded-components'

export const Title = styled.h2`
  color: ${themeColor(BaseColor.shade_70)};
  ${Typography.labelLarge};
  ${Typography.textSemiStrong};
  ${transitions(['color'])};

  &:only-child {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    // Fallback to -webkit-line-clamp
    max-height: ${rem(LineHeight.medium * 2)};
    margin: -1px 0; // To be the same as image
  }

  &:not(:only-child) {
    ${textEllipsis};
  }
`

export const SubTitle = styled.div`
  min-width: 0;
  margin-left: ${rem(2)};
  ${Typography.labelSmall};
  ${textEllipsis};
`

export const Description = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  // Fallback to -webkit-line-clamp
  max-height: ${rem(LineHeight.small * 3)};
  ${Typography.labelSmall};
  ${Typography.textRegular};
`

interface ContainerProps {
  $isLink?: boolean
}

export const Container = styled.article<ContainerProps>`
  display: flex;
  flex-direction: column;

  ${({ $isLink }) =>
    $isLink &&
    css`
      ${linkReset};
      cursor: pointer;

      &:hover {
        ${elevation(4)}

        ${Title} {
          color: ${themeColor(BaseColor.shade_60)};
        }
      }
    `};

  min-height: ${rem(120)};
  padding: ${spacing(0.75)};
  color: ${themeColor(BaseColor.shade_50)};
  background-color: ${themeColor(BaseColor.shade_0)};
  border-radius: ${borderRadius};
  ${elevation(2)}
  ${transitions(['box-shadow'])};
`

export const Section = styled.div`
  margin-top: ${rem(10)};
`

export const Header = styled.div`
  display: flex;
  align-items: center;

  ${Section} {
    flex-grow: 1;
    min-width: 0;
    margin-top: 0;
    margin-left: ${rem(6)};
  }
`

export const NotebookHeader = styled.div`
  ${Header} {
    flex-direction: row;
  }
`

export const SubHeader = styled(Header)`
  margin-top: ${rem(2)};
`

interface ImageContainerProps {
  isBright?: boolean
}

export const ImageContainer = styled.div<ImageContainerProps>`
  flex-shrink: 0;
  width: ${rem(36)};
  height: ${rem(36)};
  margin: 1px 0; // To be same height as Header section
  border-radius: 50%;
  color: ${themeColor(BaseColor.shade_0)};
  background-color: ${({ isBright }) => (isBright === false ? 'transparent' : themeColor(BaseColor.shade_20))};

  &:only-child {
    width: ${rem(60)};
    height: ${rem(60)};
    margin: auto;
  }
`

export const TagsContainer = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  margin: 0 ${rem(-5)};
`

export const Tag = styled.div`
  ${Typography.labelSmall};
  ${Typography.textRegular};
  ${transitions(['color'])};
  background-color: ${themeColor(BaseColor.shade_20)};
  padding: ${rem(1)} ${rem(5)};
  display: flex;
  align-items: center;
  justify-content: center;

  margin: ${rem(3)};
  border-radius: ${rem(2)};

  &:hover {
    color: ${themeColor(BaseColor.shade_70)};
  }
`

export const NotebookImageContainer = styled.div`
  width: 100%;
  height: ${rem(130)};
  margin-bottom: ${rem(10)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 50% 50%;
  }
`

export const ImagePlaceholder = styled.div`
  background-color: ${themeColor(BaseColor.shade_20)};
  height: 100%;
  width: 100%;
  align-content: center;
  justify-items: center;
  align-items: center;
  justify-content: center;
  display: flex;
`

export const Date = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  // Fallback to -webkit-line-clamp
  max-height: ${rem(LineHeight.small * 3)};
  ${Typography.labelSmall};
  ${Typography.textRegular};

  display: flex;
  flex: 1;
  align-items: flex-end;
`
export const EndSection = styled.div`
  margin-top: ${rem(10)};
  display: flex;
  flex: 1;
  align-items: flex-end;
`
